import React, { useState } from "react";
import { Box, Typography, Slider, Switch, Button, TextField, FormControlLabel, Select, MenuItem, InputLabel, FormControl, Chip, Grid } from "@mui/material";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis } from "recharts";

const variableCategories = {
  "Avatar Control": ["voiceType", "avatarStyle", "expression"],
  "Alzheimer's Struggles": ["memoryRecall", "confusionLevel"],
  "Alzheimer's Stages": ["mci", "moderate", "advanced"],
  Personality: ["openness", "conscientiousness", "extraversion", "agreeableness", "neuroticism"],
  Demographics: ["age", "gender", "ethnicity"],
  Mood: ["currentMood", "moodStability"],
  Health: ["hasDiabetes", "hasHeartCondition"],
  Situation: ["situationType"],
  Triggers: ["happyTriggers"],
  Emotions: ["mad", "sad", "glad", "afraid"],
  "Emotional Intelligence": ["stories", "stimuli", "wants", "feelings"],
  "Additional Traits": ["humorLevel", "smartAlecLevel", "intelligenceLevel", "passiveAggressive"],
  Memories: ["going to the candy store in Jackson, Missippi", "jumping trees in Mason, MI", "going to the michael jackson concert", "going to watch michael jordan play"],
  FavoritesToDo: ["Chess", "Spaids", "Bingo", "Watch Seinfield", "Review Pictures & Memories"],
  Difficulty: ["interactionDifficulty"]
};

const selectOptions = {
  voiceType: ["default", "soft", "cheerful"],
  avatarStyle: ["realistic", "cartoon", "minimal"],
  expression: ["neutral", "happy", "confused"],
  alzheimersStage: ["preclinical", "MCI", "early", "middle", "late"],
  gender: ["male", "female", "non-binary"],
  ethnicity: ["caucasian", "african american", "asian", "latino", "other"],
  currentMood: ["neutral", "happy", "sad", "angry", "afraid"],
  situationType: ["meal time", "morning greeting", "bathing time", "doctor appointment", "cognitive test"],
  intelligenceLevel: ["young child", "teen", "adult", "older adult"],
  passiveAggressive: ["passive", "neutral", "aggressive"],
  interactionDifficulty: ["easy", "medium", "hard"]
};

const defaultVariables = {
  voiceType: "default",
  avatarStyle: "realistic",
  expression: "neutral",
  alzheimersStage: "early",
  memoryRecall: 70,
  confusionLevel: 30,
  openness: 50,
  conscientiousness: 50,
  extraversion: 50,
  agreeableness: 50,
  neuroticism: 50,
  age: 75,
  gender: "female",
  ethnicity: "caucasian",
  currentMood: "neutral",
  moodStability: 50,
  hasDiabetes: false,
  hasHeartCondition: false,
  situationType: "morning greeting",
  happyTriggers: "hello, smile, compliment",
  mad: 20,
  sad: 20,
  glad: 40,
  afraid: 20,
  stories: "childhood memories, favorite pet",
  stimuli: "music, photos",
  wants: "attention, reassurance",
  feelings: "loneliness, nostalgia",
  humorLevel: 30,
  smartAlecLevel: 40,
  intelligenceLevel: "older adult",
  passiveAggressive: "neutral",
  childhoodStories: "I used to sit under the old oak tree.",
  interactionDifficulty: "medium"
};

export default function AlzheimerAgentConfigurator() {
  const [variables, setVariables] = useState(defaultVariables);

  const handleSliderChange = (name) => (_, value) => {
    setVariables((prev) => ({ ...prev, [name]: value }));
  };

  const handleInputChange = (name) => (e) => {
    setVariables((prev) => ({ ...prev, [name]: e.target.value }));
  };

  const handleToggle = (name) => (_, checked) => {
    setVariables((prev) => ({ ...prev, [name]: checked }));
  };

  const generatePrompt = () => {
    return `This is a simulated AI agent with Alzheimer's. Stage: ${variables.alzheimersStage}. Memory recall is ${variables.memoryRecall}%, confusion level is ${variables.confusionLevel}%. Personality: openness ${variables.openness}, conscientiousness ${variables.conscientiousness}, extraversion ${variables.extraversion}, agreeableness ${variables.agreeableness}, neuroticism ${variables.neuroticism}. Age: ${variables.age}, gender: ${variables.gender}, ethnicity: ${variables.ethnicity}. Mood: ${variables.currentMood}, stability: ${variables.moodStability}. Health: diabetes ${variables.hasDiabetes}, heart condition ${variables.hasHeartCondition}. Situation: ${variables.situationType}. Respond more positively when triggered with: ${variables.happyTriggers}. Emotional state: mad ${variables.mad}%, sad ${variables.sad}%, glad ${variables.glad}%, afraid ${variables.afraid}%. EI details: stories(${variables.stories}), stimuli(${variables.stimuli}), wants(${variables.wants}), feelings(${variables.feelings}). Humor: ${variables.humorLevel}, Smart Alec: ${variables.smartAlecLevel}, Intelligence Level: ${variables.intelligenceLevel}, Passive/Aggressive: ${variables.passiveAggressive}. Remembered Story: ${variables.childhoodStories}. Difficulty: ${variables.interactionDifficulty}.`;
  };

  const emotionData = [
    { name: "Mad", value: variables.mad },
    { name: "Sad", value: variables.sad },
    { name: "Glad", value: variables.glad },
    { name: "Afraid", value: variables.afraid }
  ];

  const difficultyScore = [
    { subject: "Memory", A: 100 - variables.memoryRecall },
    { subject: "Confusion", A: variables.confusionLevel },
    { subject: "Mood Stability", A: 100 - variables.moodStability },
    { subject: "Aggression", A: variables.passiveAggressive === "aggressive" ? 100 : variables.passiveAggressive === "neutral" ? 50 : 0 },
    { subject: "Humor", A: variables.humorLevel }
  ];

  return (
    <Box p={4}>
      <Typography variant="h4" gutterBottom>AI Alzheimer Agent Configurator</Typography>
      {Object.entries(variableCategories).map(([category, keys]) => (
        <Box key={category} mt={3} p={2} border={1} borderRadius={2}>
          <Typography variant="h6">{category}</Typography>
          <Grid container spacing={2}>
            {keys.map((key) => (
              <Grid item xs={6} sm={4} key={key}>
                {typeof defaultVariables[key] === "boolean" ? (
                  <FormControlLabel
                    control={<Switch checked={variables[key]} onChange={handleToggle(key)} />}
                    label={key}
                  />
                ) : typeof defaultVariables[key] === "number" ? (
                  <Box>
                    <Typography>{key}</Typography>
                    <Slider
                      value={variables[key]}
                      onChange={handleSliderChange(key)}
                      step={1}
                      min={0}
                      max={100}
                    />
                  </Box>
                ) : selectOptions[key] ? (
                  <FormControl fullWidth>
                    <InputLabel>{key}</InputLabel>
                    <Select
                      value={variables[key]}
                      label={key}
                      onChange={handleInputChange(key)}
                    >
                      {selectOptions[key].map((option) => (
                        <MenuItem value={option} key={option}>{option}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                  <TextField
                    fullWidth
                    label={key}
                    value={variables[key]}
                    onChange={handleInputChange(key)}
                  />
                )}
              </Grid>
            ))}
          </Grid>
        </Box>
      ))}

      <Box mt={4}>
        <Typography variant="h6">Emotional Visualization</Typography>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={emotionData}>
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip />
            <Bar dataKey="value" />
          </BarChart>
        </ResponsiveContainer>
      </Box>

      <Box mt={4}>
        <Typography variant="h6">Interaction Difficulty Radar</Typography>
        <ResponsiveContainer width="100%" height={300}>
          <RadarChart data={difficultyScore}>
            <PolarGrid />
            <PolarAngleAxis dataKey="subject" />
            <PolarRadiusAxis angle={30} domain={[0, 100]} />
            <Radar name="Score" dataKey="A" stroke="#8884d8" fill="#8884d8" fillOpacity={0.6} />
          </RadarChart>
        </ResponsiveContainer>
      </Box>

      <Box mt={4}>
        <Typography variant="h6">Generated AI Prompt</Typography>
        <TextField
          multiline
          fullWidth
          rows={6}
          value={generatePrompt()}
          variant="outlined"
        />
        <Button
          variant="contained"
          sx={{ mt: 2 }}
          onClick={() => navigator.clipboard.writeText(generatePrompt())}
        >
          Copy Prompt
        </Button>
      </Box>
    </Box>
  );
}

